package com.steamstreet.vegasful.browser.account.subscriptions

import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.web.ClientController
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import org.w3c.dom.Element

/**
 * The "save my favourites" button under the preference picker.
 *
 * Likes are already saved as they are tapped, so this does not save anything. What it does is
 * rebuild the recommendation profile *now*: a like schedules a rebuild that is deliberately delayed
 * so that picking ten things in twenty seconds does not mean ten rebuilds, and For You would
 * otherwise stay empty for minutes after somebody had just told us what they like.
 *
 * The button is the explicit "I have finished choosing" that the debounce timer is otherwise trying
 * to guess at.
 */
class SavePreferencesController(
    private val element: Element,
    private val context: CoroutineScope
) : ClientController {

    override suspend fun run() {
        element.onClick {
            save()
        }
    }

    private val label: Element?
        get() = element.querySelector(".$LABEL_CLASS")

    private suspend fun save() {
        // Disabled while in flight. The rebuild is a synchronous read of everything the user has
        // done, so it is quick but not instant, and a second press would only wait again.
        if (element.hasAttribute("data-busy")) return
        element.setAttribute("data-busy", "1")
        element.addClass("busy")

        // Say what is happening rather than only dimming. The wait spans a profile rebuild *and* a
        // page load, which is long enough that an unexplained inert button reads as a failure. The
        // words also promise the right thing: the likes were saved as they were tapped, so what
        // this is waiting on is the guide being assembled.
        val original = label?.textContent
        label?.textContent = "Building your guide…"

        try {
            GraphQL.mutation {
                subscriptions {
                    buildRecommendations
                }
            }
            // Deliberately not restored on success: the reload that follows is part of the same
            // wait, and flicking the label back to its resting state first would suggest the
            // button had finished when the page had not.
            window.location.reload()
        } catch (t: Throwable) {
            // Leave the user able to try again rather than stuck on a dead button. The likes
            // themselves are already saved, so nothing has been lost either way.
            console.warn("Unable to build recommendations", t)
            label?.textContent = original ?: "Save my favorites"
            element.removeAttribute("data-busy")
            element.removeClass("busy")
        }
    }

    companion object {
        /** Mirrors `SAVE_PREFERENCES_LABEL` in the page that renders the button. */
        const val LABEL_CLASS = "save-preferences-label"
    }
}
